/**
 * @fileoverview Edit mode content for patient details modal
 */
import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { intl, DmsCheckBox, DmsTextFieldPhone, fetchData } from "dmsbasereact";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import {
  Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormGroup, FormControlLabel, Checkbox, Typography, FormLabel, Radio, RadioGroup
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import DmsDatePicker from "../DmsDatePicker/DmsDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { DmsInfoCard } from "../DmsDetailModal/DmsInfoCard";
import { getBrazilianStateOptions } from "../../constants/BrazilianStates";
import { getGenderOptions } from "../../constants/GenderEnum";
import DmsSelectRGIssuingAgency from "../DmsSelectRGIssuingAgency/DmsSelectRGIssuingAgency";
import { DmsTextFieldCep } from "../DmsTextFieldCep";
import { DmsTextFieldCpf } from "../DmsTextFieldCpf/DmsTextFieldCpf";
import dayjs from "dayjs";

/**
 * Edit mode content for editing patient details
 * Receives `data`, `formData`, `onFieldChange` from DmsDetailModal
 */
export const EditModeContent = ({ data, formData, onFieldChange }) => {
  const patient = data;

  const [highlyEffectiveMethodOptions, setHighlyEffectiveMethodOptions] = useState([]);
  const [barrierMethodOptions, setBarrierMethodOptions] = useState([]);

  // Fetch contraceptive methods from backend
  useEffect(() => {
    const fetchContraceptiveMethods = async () => {
      try {
        const response = await fetchData('/contraceptive_method/all', 'GET', true);
        if (response?.data) {
          const methods = response.data.data;
          const methodsArray = Array.isArray(methods) ? methods : [];

          // Filter by category
          const highlyEffective = methodsArray
            .filter(method => method.category === 'highly_effective')
            .map(method => ({ value: method.pk, label: method.reason }));

          const barrier = methodsArray
            .filter(method => method.category === 'barrier')
            .map(method => ({ value: method.pk, label: method.reason }));

          setHighlyEffectiveMethodOptions(highlyEffective);
          setBarrierMethodOptions(barrier);
        }
      } catch (error) {
        console.error("Error fetching contraceptive methods:", error);
      }
    };

    fetchContraceptiveMethods();
  }, []);

  // AR-009 Item 9.13: Check if patient is under 18 years old
  const isMinor = useMemo(() => {
    if (!formData.birth_date) return false;
    const birthDate = dayjs(formData.birth_date);
    if (!birthDate.isValid()) return false;
    const age = dayjs().diff(birthDate, 'year');
    return age < 18;
  }, [formData.birth_date]);

  // Helper to check if a required field is empty
  const isFieldInvalid = (fieldValue) => {
    return !fieldValue || fieldValue.toString().trim() === '';
  };

  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              label={intl.get("PatientID").d("Patient ID")}
              value={patient.patient_identifier || ""}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label={intl.get("Name").d("Name")}
              value={formData.name || ""}
              onChange={(e) => onFieldChange("name", e.target.value)}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label={intl.get("CPF").d("CPF")}
              value={formData.cpf || ""}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label={intl.get("RG").d("RG")}
              value={formData.rg || ""}
              onChange={(e) => onFieldChange("rg", e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <DmsSelectRGIssuingAgency
              value={formData.rg_issuing_agency || ""}
              onChange={(value) => onFieldChange("rg_issuing_agency", value)}
              size="small"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DmsDatePicker
                label={intl.get("RGIssuingDate").d("RG Issuing Date")}
                value={formData.rg_issuing_date ? dayjs(formData.rg_issuing_date) : null}
                onChange={(newValue) => onFieldChange("rg_issuing_date", newValue ? newValue.format("YYYY-MM-DD") : "")}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DmsDatePicker
                label={intl.get("BirthDate").d("Birth Date")}
                value={formData.birth_date ? dayjs(formData.birth_date) : null}
                onChange={(newValue) => onFieldChange("birth_date", newValue ? newValue.format("YYYY-MM-DD") : "")}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true, size: "small", required: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("Gender").d("Gender")}</InputLabel>
              <Select
                value={formData.gender || ""}
                label={intl.get("Gender").d("Gender")}
                onChange={(e) => onFieldChange("gender", e.target.value)}
              >
                {getGenderOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Contact Information */}
      <DmsInfoCard icon={<ContactPhoneIcon />} title={intl.get("ContactInfo").d("Contact Information")}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={intl.get("Email").d("Email")}
              value={formData.email || ""}
              onChange={(e) => onFieldChange("email", e.target.value)}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <DmsTextFieldPhone
              label={intl.get("Phone").d("Phone")}
              value={formData.phone || ""}
              onChange={(e) => onFieldChange("phone", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <DmsTextFieldPhone
              label={intl.get("HomePhone").d("Home Phone")}
              value={formData.home_phone || ""}
              onChange={(e) => onFieldChange("home_phone", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* LGPD Consent Information */}
      <DmsInfoCard icon={<VerifiedUserIcon />} title={intl.get("ConsentInformation").d("Informações de Consentimento")}>
        <Grid container spacing={2}>
          {/* Read-only acceptance date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={intl.get("ConsentAcceptanceDate").d("Data de Aceitação do Consentimento")}
              value={patient.created_on ? dayjs(patient.created_on).format("DD/MM/YYYY HH:mm") : "-"}
              disabled
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Editable contact authorization */}
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                {intl.get("ContactAuthorization").d("Autoriza Contato")} *
              </FormLabel>
              <RadioGroup
                row
                value={
                  formData.contact_authorization === null
                    ? ''
                    : formData.contact_authorization
                    ? 'yes'
                    : 'no'
                }
                onChange={(e) => onFieldChange('contact_authorization', e.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" color="success" />}
                  label={intl.get("Yes").d("Sim")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" color="error" />}
                  label={intl.get("No").d("Não")}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Medical Information - only for female patients */}
      {formData.gender === "F" && (
        <DmsInfoCard icon={<LocalHospitalIcon />} title={intl.get("MedicalInfo").d("Medical Information")}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DmsCheckBox
                label={intl.get("FertilityPotencial").d("Fertility Potential")}
                checked={formData.has_pregnancy_potential || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onFieldChange("has_pregnancy_potential", isChecked);
                  if (isChecked) {
                    onFieldChange("selectedInfertilityReason", null);
                  } else {
                    onFieldChange("highlyEffectiveMethods", []);
                    onFieldChange("barrierMethods", []);
                    onFieldChange("menstrual_cycle_regular", false);
                  }
                }}
              />
            </Grid>
            {formData.has_pregnancy_potential && (
              <Grid item xs={12}>
                <DmsCheckBox
                  label={intl.get("MenstrualCycleRegular").d("Menstrual Cycle Regular")}
                  checked={formData.menstrual_cycle_regular || false}
                  onChange={(e) => onFieldChange("menstrual_cycle_regular", e.target.checked)}
                />
              </Grid>
            )}
            {formData.has_pregnancy_potential && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                    {intl.get("ContraceptiveMethodsRequired").d("Contraceptive Methods (Required)")} *
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {intl.get("ContraceptiveMethodsInstruction").d("Select at least one highly effective method and one barrier method that the patient is using.")}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    height: '100%'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'success.dark' }}>
                      {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")} *
                    </Typography>
                    <FormGroup>
                      {highlyEffectiveMethodOptions.map((method) => (
                        <FormControlLabel
                          key={method.value}
                          control={
                            <Checkbox
                              checked={formData.highlyEffectiveMethods?.includes(method.value) || false}
                              onChange={(e) => {
                                const current = formData.highlyEffectiveMethods || [];
                                if (e.target.checked) {
                                  onFieldChange("highlyEffectiveMethods", [...current, method.value]);
                                } else {
                                  onFieldChange("highlyEffectiveMethods", current.filter(m => m !== method.value));
                                }
                              }}
                              size="small"
                              color="success"
                            />
                          }
                          label={<Typography variant="body2">{method.label}</Typography>}
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    height: '100%'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'warning.dark' }}>
                      {intl.get("BarrierMethods").d("Barrier Method")} *
                    </Typography>
                    <FormGroup>
                      {barrierMethodOptions.map((method) => (
                        <FormControlLabel
                          key={method.value}
                          control={
                            <Checkbox
                              checked={formData.barrierMethods?.includes(method.value) || false}
                              onChange={(e) => {
                                const current = formData.barrierMethods || [];
                                if (e.target.checked) {
                                  onFieldChange("barrierMethods", [...current, method.value]);
                                } else {
                                  onFieldChange("barrierMethods", current.filter(m => m !== method.value));
                                }
                              }}
                              size="small"
                              color="warning"
                            />
                          }
                          label={<Typography variant="body2">{method.label}</Typography>}
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Grid>
              </>
            )}
            {!formData.has_pregnancy_potential && (
              <Grid item xs={12}>
                <DmsAutocomplete
                  properties={{ label: 'reason', value: 'pk' }}
                  needs_authentication={true}
                  url={'/infertility_reason/all'}
                  onChange={(selectedOption) => onFieldChange("selectedInfertilityReason", selectedOption)}
                  value={formData.selectedInfertilityReason || ""}
                  label={intl.get('InfertilityReason').d('Infertility Reason') + " *"}
                  required
                />
              </Grid>
            )}
          </Grid>
        </DmsInfoCard>
      )}

      {/* Address Information */}
      <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <DmsTextFieldCep
              name="zip_code"
              label={intl.get("ZipCode").d("Zip Code")}
              value={formData.zip_code || ""}
              onChange={(e) => onFieldChange("zip_code", e.target.value)}
              fullWidth
              size="small"
              required
              error={isFieldInvalid(formData.zip_code)}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label={intl.get("Address").d("Address")}
              value={formData.address || ""}
              onChange={(e) => onFieldChange("address", e.target.value)}
              fullWidth
              size="small"
              required
              error={isFieldInvalid(formData.address)}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={intl.get("Number").d("Number")}
              value={formData.address_number || ""}
              onChange={(e) => onFieldChange("address_number", e.target.value)}
              fullWidth
              size="small"
              required
              error={isFieldInvalid(formData.address_number)}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={intl.get("City").d("City")}
              value={formData.city || ""}
              onChange={(e) => onFieldChange("city", e.target.value)}
              fullWidth
              size="small"
              required
              error={isFieldInvalid(formData.city)}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small" required error={isFieldInvalid(formData.address_state)}>
              <InputLabel>{intl.get("State").d("State")}</InputLabel>
              <Select
                value={formData.address_state || ""}
                label={intl.get("State").d("State")}
                onChange={(e) => onFieldChange("address_state", e.target.value)}
                required
                error={isFieldInvalid(formData.address_state)}
              >
                <MenuItem value="">{intl.get("Select").d("Select")}</MenuItem>
                {getBrazilianStateOptions().map(({ value: code, label }) => (
                  <MenuItem key={code} value={code}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DmsInfoCard>

      {/* Responsible Legal Information */}
      <DmsInfoCard icon={<SupervisorAccountIcon />} title={intl.get("ResponsibleLegal").d("Legal Guardian")}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DmsCheckBox
              label={intl.get("HasResponsibleLegal").d("Has Responsible Legal")}
              checked={formData.hasResponsibleLegal || isMinor || false}
              disabled={isMinor}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onFieldChange("hasResponsibleLegal", isChecked);
                if (!isChecked) {
                  onFieldChange("responsibleLegal_name", "");
                  onFieldChange("responsibleLegal_cpf", "");
                  onFieldChange("responsibleLegal_rg", "");
                  onFieldChange("responsibleLegal_rg_issuing_agency", "");
                  onFieldChange("responsibleLegal_birth_date", "");
                  onFieldChange("responsibleLegal_email", "");
                  onFieldChange("responsibleLegal_phone", "");
                  onFieldChange("sameAddressAsPatient", false);
                }
              }}
            />
          </Grid>

          {(formData.hasResponsibleLegal || isMinor) && (
            <>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("Name").d("Name")}
                  value={formData.responsibleLegal_name || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_name", e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <DmsTextFieldCpf
                  label={intl.get("CPF").d("CPF")}
                  value={formData.responsibleLegal_cpf || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_cpf", e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={!!formData.responsibleLegal_pk}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("RG").d("RG")}
                  value={formData.responsibleLegal_rg || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_rg", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={6}>
                <DmsSelectRGIssuingAgency
                  value={formData.responsibleLegal_rg_issuing_agency || ""}
                  onChange={(value) => onFieldChange("responsibleLegal_rg_issuing_agency", value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DmsDatePicker
                    label={intl.get("BirthDate").d("Birth Date")}
                    value={formData.responsibleLegal_birth_date ? dayjs(formData.responsibleLegal_birth_date) : null}
                    onChange={(newValue) => onFieldChange("responsibleLegal_birth_date", newValue ? newValue.format("YYYY-MM-DD") : "")}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("Email").d("Email")}
                  value={formData.responsibleLegal_email || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_email", e.target.value)}
                  fullWidth
                  size="small"
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <DmsTextFieldPhone
                  label={intl.get("Phone").d("Phone")}
                  value={formData.responsibleLegal_phone || ""}
                  onChange={(e) => onFieldChange("responsibleLegal_phone", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Same address checkbox */}
              <Grid item xs={12}>
                <DmsCheckBox
                  label={intl.get("SameAddressAsPatient").d("Same address as patient")}
                  checked={formData.sameAddressAsPatient || false}
                  onChange={(e) => onFieldChange("sameAddressAsPatient", e.target.checked)}
                />
              </Grid>

              {/* Responsible Legal Address Fields - shown when not same address */}
              {!formData.sameAddressAsPatient && (
                <>
                  <Grid item xs={4}>
                    <DmsTextFieldCep
                      name="responsibleLegal_zip_code"
                      label={intl.get("ZipCode").d("Zip Code")}
                      value={formData.responsibleLegal_zip_code || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_zip_code", e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={isFieldInvalid(formData.responsibleLegal_zip_code)}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      label={intl.get("Address").d("Address")}
                      value={formData.responsibleLegal_address || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_address", e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={isFieldInvalid(formData.responsibleLegal_address)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label={intl.get("Number").d("Number")}
                      value={formData.responsibleLegal_number || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_number", e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={isFieldInvalid(formData.responsibleLegal_number)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label={intl.get("City").d("City")}
                      value={formData.responsibleLegal_city || ""}
                      onChange={(e) => onFieldChange("responsibleLegal_city", e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={isFieldInvalid(formData.responsibleLegal_city)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small" required error={isFieldInvalid(formData.responsibleLegal_state)}>
                      <InputLabel>{intl.get("State").d("State")}</InputLabel>
                      <Select
                        value={formData.responsibleLegal_state || ""}
                        label={intl.get("State").d("State")}
                        onChange={(e) => onFieldChange("responsibleLegal_state", e.target.value)}
                        required
                        error={isFieldInvalid(formData.responsibleLegal_state)}
                      >
                        {getBrazilianStateOptions().map(({ value: code, label }) => (
                          <MenuItem key={code} value={code}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </DmsInfoCard>
    </Box>
  )
};

EditModeContent.propTypes = {
  data: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

